import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { NEUTRAL, SEVERITY_COLORS } from '../theme';

const formatTs = (ts) => {
  if (!ts) return '-';
  if (typeof ts === 'string') return ts;
  if (typeof ts === 'number') return new Date(ts * 1000).toLocaleString('en-US', { hour12: false });
  const seconds = ts._seconds ?? ts.seconds;
  if (typeof seconds === 'number') return new Date(seconds * 1000).toLocaleString('en-US', { hour12: false });
  return '-';
};

const pickLabelKeys = (labels) => {
  const preferred = [
    'exported_project_id',
    'project_id',
    'mac_address',
    'instance_name',
    'value',
    'subscription_id',
    'topic_id',
    'service_name',
    'resource_name',
  ];
  const result = [];
  preferred.forEach((k) => {
    if (labels?.[k] !== undefined && labels?.[k] !== null && String(labels[k]).trim() !== '') {
      if (k === 'value') {
        const raw = labels[k];
        const num = typeof raw === 'number' ? raw : Number(raw);
        if (Number.isFinite(num)) {
          result.push([k, num.toFixed(1)]);
          return;
        }
      }
      result.push([k, String(labels[k])]);
    }
  });
  return result;
};

// Component display names
const componentDisplayNames = {
  pubsub: 'Pub/Sub',
  dataflow: 'Dataflow',
  dataform: 'Dataform',
  firestore: 'Firestore',
  cloud_functions: 'Cloud Functions',
  gke: 'GKE (Kubernetes)',
  edge_devices: 'Edge Devices',
  network_manager: 'Network Manager',
  lms: 'LMS',
  other: 'Other',
};

// Severity colors
const severityColors = SEVERITY_COLORS;

// Alert type is a category, not a status, so it stays neutral.
const alertTypeColors = {
  error: { bg: NEUTRAL[100], color: NEUTRAL[700] },
  performance: { bg: NEUTRAL[100], color: NEUTRAL[700] },
  resource: { bg: NEUTRAL[100], color: NEUTRAL[700] },
  availability: { bg: NEUTRAL[100], color: NEUTRAL[700] },
};

const AlertDetailsDialog = ({ open, onClose, event, demoDataId }) => {
  const incidentUrl = event?.raw?.incident?.url || null;
  const metricType = event?.raw?.incident?.metric?.type || null;
  const isNetworkManagerMetric = Boolean(metricType && metricType.includes('custom.googleapis.com/network_manager/'));
  const isLmsMetric = Boolean(metricType && metricType.includes('custom.googleapis.com/lms/'));

  const metricHighlights = useMemo(() => pickLabelKeys(event?.metricLabels), [event]);
  
  const severity = event?.severity || 'warning';
  const severityStyle = severityColors[severity] || {};
  const alertType = event?.alertType || 'error';
  const alertTypeStyle = alertTypeColors[alertType] || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        Alert Details
      </DialogTitle>
      <DialogContent dividers {...(demoDataId ? { 'data-demo-id': demoDataId } : {})}>
        {!event ? (
          <Typography sx={{ color: 'text.secondary' }}>No alert selected.</Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ minWidth: 280 }}>
                <Typography sx={{ fontWeight: 600 }}>{event.policyName || '-'}</Typography>
                <Typography sx={{ color: 'text.secondary', mt: 0.5, mb: 1 }}>
                  {event.conditionName || '-'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {event.component && (
                    <Chip
                      label={componentDisplayNames[event.component] || event.component}
                      size="small"
                      sx={{ height: 22, fontSize: '0.75rem' }}
                    />
                  )}
                  {severity && (
                    <Chip
                      label={severity.toUpperCase()}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        bgcolor: severityStyle.bg,
                        color: severityStyle.color,
                      }}
                    />
                  )}
                  {alertType && (
                    <Chip
                      label={alertType}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        bgcolor: alertTypeStyle.bg,
                        color: alertTypeStyle.color,
                      }}
                    />
                  )}
                </Box>
              </Box>
              {incidentUrl && (
                <Box sx={{ textAlign: 'right' }}>
                  <Link href={incidentUrl} target="_blank" rel="noreferrer">
                    Open in Cloud Monitoring
                  </Link>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ width: 160, color: 'text.secondary' }}>Project</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{event.projectId || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>Env</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{event.env || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>State</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{event.state || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>Received</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{formatTs(event.receivedAt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>Started</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{formatTs(event.startedAt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>Ended</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{formatTs(event.endedAt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>Incident ID</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{event.incidentId || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', verticalAlign: 'top' }}>Summary</TableCell>
                  <TableCell>{event.summary || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>Metric Type</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{metricType || '-'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {metricHighlights.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography sx={{ fontWeight: 600, mb: 1 }}>Device / Metric Details</Typography>
                <Table size="small">
                  <TableBody>
                    {metricHighlights.map(([k, v]) => (
                      <TableRow key={k}>
                        <TableCell sx={{ width: 220, color: 'text.secondary' }}>{k}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{v}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}

            {event?.deviceDetails && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  {isLmsMetric ? 'LMS Device Details' : 'Network Manager Device Details'}
                </Typography>
                
                {event.deviceDetails.error && (
                  <Typography sx={{ color: 'error.main', mb: 2 }}>
                    Error loading device details: {event.deviceDetails.error}
                  </Typography>
                )}

                {event.deviceDetails.aggregatedMetrics && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Aggregated Metrics</Typography>
                    <Table size="small">
                      <TableBody>
                        {isLmsMetric ? (
                          <>
                            <TableRow>
                              <TableCell sx={{ width: 220, color: 'text.secondary' }}>Lamps Total</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.lamps_total ?? '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary' }}>Lamps Online</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.lamps_online ?? '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary' }}>Lamps Offline</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.lamps_offline ?? '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary' }}>Offline Percentage</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.lamps_offline_percentage !== undefined
                                  ? `${Number(event.deviceDetails.aggregatedMetrics.lamps_offline_percentage).toFixed(2)}%`
                                  : '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary' }}>Lamps ON</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.lamps_on ?? '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary' }}>Lamps OFF</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.lamps_off ?? '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary' }}>ON Percentage</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.lamps_on_percentage !== undefined
                                  ? `${Number(event.deviceDetails.aggregatedMetrics.lamps_on_percentage).toFixed(2)}%`
                                  : '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary' }}>OFF Percentage</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.lamps_off_percentage !== undefined
                                  ? `${Number(event.deviceDetails.aggregatedMetrics.lamps_off_percentage).toFixed(2)}%`
                                  : '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary' }}>Assigned Gateways</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.active_gateways ?? '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary' }}>Connected Gateways</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.gateway_connected_count ?? '-'}
                              </TableCell>
                            </TableRow>
                          </>
                        ) : (
                          <>
                            {/* Network Manager core device counts */}
                            <TableRow>
                              <TableCell sx={{ width: 220, color: 'text.secondary' }}>Devices Total</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.devices_total || '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary' }}>Devices Connected</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.devices_connected || '-'}
                              </TableCell>
                            </TableRow>
                          </>
                        )}

                        {/* Only show offline fields for offline alerts */}
                        {metricType && metricType.includes('custom.googleapis.com/network_manager/devices/offline') && (
                          <>
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary' }}>Devices Offline</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.devices_offline || '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary' }}>Offline Percentage</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.offline_percentage !== undefined
                                  ? `${Number(event.deviceDetails.aggregatedMetrics.offline_percentage).toFixed(2)}%`
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          </>
                        )}

                        {/* Only show provisioning mismatch fields for provisioning alerts */}
                        {metricType && metricType.includes('custom.googleapis.com/network_manager/provisioning/') && (
                          event.deviceDetails.aggregatedMetrics.mismatch_count !== undefined && (
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary' }}>Provisioning Mismatches</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {event.deviceDetails.aggregatedMetrics.mismatch_count || 0}
                                {event.deviceDetails.aggregatedMetrics.mismatch_percentage !== undefined && (
                                  <span> ({Number(event.deviceDetails.aggregatedMetrics.mismatch_percentage).toFixed(2)}%)</span>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {metricType &&
                  metricType.includes('custom.googleapis.com/network_manager/devices/offline') &&
                  event.deviceDetails.offlineDevices &&
                  event.deviceDetails.offlineDevices.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>
                      Offline Devices ({event.deviceDetails.offlineDevices.length})
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Serial Number</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Model</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Device Type</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Last Seen</TableCell>
                        </TableRow>
                        {event.deviceDetails.offlineDevices.slice(0, 10).map((device, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontFamily: 'monospace' }}>
                              {device.serial_number || '-'}
                            </TableCell>
                            <TableCell>{device.model || '-'}</TableCell>
                            <TableCell>{device.device_type || '-'}</TableCell>
                            <TableCell>
                              {device.last_seen
                                ? new Date(device.last_seen).toLocaleString()
                                : device.time_since_last_seen_sec
                                ? `${Math.floor(device.time_since_last_seen_sec / 3600)}h ${Math.floor((device.time_since_last_seen_sec % 3600) / 60)}m ago`
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {event.deviceDetails.offlineDevices.length > 10 && (
                      <Typography sx={{ color: 'text.secondary', mt: 1, fontSize: '0.875rem' }}>
                        ... and {event.deviceDetails.offlineDevices.length - 10} more devices
                      </Typography>
                    )}
                  </Box>
                )}

                {metricType &&
                  metricType.includes('custom.googleapis.com/lms/lamps/offline') &&
                  event.deviceDetails.offlineLamps &&
                  event.deviceDetails.offlineLamps.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>
                      Offline Lamps ({event.deviceDetails.offlineLamps.length})
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Dimmer Serial</TableCell>
                        </TableRow>
                        {event.deviceDetails.offlineLamps.slice(0, 25).map((serial, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{String(serial)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {event.deviceDetails.offlineLamps.length > 25 && (
                      <Typography sx={{ color: 'text.secondary', mt: 1, fontSize: '0.875rem' }}>
                        ... and {event.deviceDetails.offlineLamps.length - 25} more lamps
                      </Typography>
                    )}
                  </Box>
                )}

                {metricType &&
                  metricType.includes('custom.googleapis.com/network_manager/provisioning/') &&
                  event.deviceDetails.provisioningMismatches &&
                  event.deviceDetails.provisioningMismatches.length > 0 && (
                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>
                      Provisioning Mismatches ({event.deviceDetails.provisioningMismatches.length})
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>MAC Address</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Device Type</TableCell>
                        </TableRow>
                        {event.deviceDetails.provisioningMismatches.slice(0, 10).map((mismatch, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Chip
                                label={mismatch.type || '-'}
                                size="small"
                                color="warning"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>
                              {mismatch.mac_address || '-'}
                            </TableCell>
                            <TableCell>{mismatch.device_type || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {event.deviceDetails.provisioningMismatches.length > 10 && (
                      <Typography sx={{ color: 'text.secondary', mt: 1, fontSize: '0.875rem' }}>
                        ... and {event.deviceDetails.provisioningMismatches.length - 10} more mismatches
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertDetailsDialog;


